// Register a lead, the list of leads and one lead's progress.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  ErrorState,
  Field,
  Input,
  PageHeader,
  Select,
  Textarea,
  toast,
} from '@tatra/ui';
import { useState } from 'react';
import { FormError, Loading } from '../components.js';
import {
  ApiError,
  errorMessage,
  get,
  post,
  type LeadDetail,
  type Project,
  type LeadRow,
} from '../lib/api.js';
import { dateOnly, dateTime, daysLeft, LEAD_STATUS, OUTCOME, rupees } from '../lib/format.js';
import { useSession } from '../lib/session.js';

const CHANNELS = [
  ['CALL', 'Phone call'],
  ['SMS', 'SMS'],
  ['WHATSAPP', 'WhatsApp'],
  ['EMAIL', 'Email'],
] as const;

export function RegisterLeadPage() {
  const { approved, config } = useSession();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const projects = useQuery({ queryKey: ['projects'], queryFn: () => get<Project[]>('/projects') });
  const [f, setF] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    projectId: '',
    typology: '',
    budgetMin: '',
    budgetMax: '',
    notes: '',
  });
  const [consent, setConsent] = useState<string[]>([]);
  const [missing, setMissing] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF({ ...f, [k]: e.target.value });
  const lakh = (v: string) => (v.trim() ? Math.round(Number(v) * 100_000 * 100) : null);

  const register = useMutation({
    mutationFn: () => {
      const budgetMin = lakh(f.budgetMin);
      const budgetMax = lakh(f.budgetMax);
      const hasRequirement = f.typology.trim() || budgetMin !== null || budgetMax !== null;
      return post<LeadDetail>('/leads', {
        name: f.name,
        phone: f.phone.trim() || null,
        email: f.email.trim() || null,
        city: f.city.trim() || null,
        projectId: f.projectId,
        notes: f.notes.trim() || null,
        requirement: hasRequirement ? { typology: f.typology.trim() || null, budgetMin, budgetMax } : null,
        consent,
      });
    },
    onSuccess: (lead) => {
      toast.success('Lead registered');
      void qc.invalidateQueries({ queryKey: ['leads'] });
      void qc.invalidateQueries({ queryKey: ['scorecard'] });
      void navigate({ to: '/leads/$id', params: { id: lead.id } });
    },
  });

  if (!approved)
    return (
      <>
        <PageHeader title="Register a lead" />
        <EmptyState
          title="You can register leads once your firm is approved"
          description="Finish your details and we will review them."
          action={
            <Link to="/profile">
              <Button>Go to your details</Button>
            </Link>
          }
        />
      </>
    );

  const err = register.error;
  // A refusal is always the same neutral answer (it never says who else holds the customer).
  const refused =
    err instanceof ApiError &&
    (err.code === 'registration_not_accepted' || err.code === 'already_registered');

  return (
    <>
      <PageHeader
        title="Register a lead"
        description={`Once accepted, the customer is protected for you for ${config?.exclusivityDays ?? 90} days.`}
      />
      {projects.isLoading ? (
        <Loading />
      ) : projects.isError ? (
        <ErrorState onRetry={() => void projects.refetch()} />
      ) : !projects.data?.length ? (
        <EmptyState
          title="No projects yet"
          description="You have not been given any project to sell. Ask your relationship manager."
        />
      ) : (
        <Card className="max-w-2xl p-4 sm:p-6">
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (!f.phone.trim() && !f.email.trim()) {
                setMissing('Add a phone number or an email so we can reach the customer.');
                return;
              }
              setMissing(null);
              register.mutate();
            }}
          >
            <Field label="Project" required>
              {(p) => (
                <Select {...p} value={f.projectId} onChange={set('projectId')} required>
                  <option value="">Choose a project</option>
                  {projects.data.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.city ? `, ${p.city}` : ''}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Customer name" required>
              {(p) => <Input {...p} value={f.name} onChange={set('name')} autoComplete="off" required />}
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Phone">
                {(p) => (
                  <Input
                    {...p}
                    type="tel"
                    inputMode="tel"
                    value={f.phone}
                    onChange={set('phone')}
                    autoComplete="off"
                  />
                )}
              </Field>
              <Field label="Email">
                {(p) => (
                  <Input {...p} type="email" value={f.email} onChange={set('email')} autoComplete="off" />
                )}
              </Field>
            </div>
            <Field label="City">
              {(p) => <Input {...p} value={f.city} onChange={set('city')} autoComplete="off" />}
            </Field>
            <fieldset className="space-y-3 rounded-lg border border-border p-3">
              <legend className="px-1 text-sm font-medium text-text-muted">
                What they are looking for (optional)
              </legend>
              <Field label="Home type" help="For example 2 BHK, plot, villa">
                {(p) => <Input {...p} value={f.typology} onChange={set('typology')} />}
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Budget from (₹ lakh)">
                  {(p) => (
                    <Input
                      {...p}
                      type="number"
                      min="0"
                      step="any"
                      inputMode="decimal"
                      value={f.budgetMin}
                      onChange={set('budgetMin')}
                    />
                  )}
                </Field>
                <Field label="Budget up to (₹ lakh)">
                  {(p) => (
                    <Input
                      {...p}
                      type="number"
                      min="0"
                      step="any"
                      inputMode="decimal"
                      value={f.budgetMax}
                      onChange={set('budgetMax')}
                    />
                  )}
                </Field>
              </div>
            </fieldset>
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium text-text-muted">
                The customer agreed to be contacted by
              </legend>
              <div className="grid grid-cols-2 gap-2">
                {CHANNELS.map(([key, label]) => (
                  <Checkbox
                    key={key}
                    label={label}
                    checked={consent.includes(key)}
                    onChange={(e) =>
                      setConsent(e.target.checked ? [...consent, key] : consent.filter((c) => c !== key))
                    }
                  />
                ))}
              </div>
              <p className="text-xs text-text-faint">
                Tick only what the customer told you they are happy with.
              </p>
            </fieldset>
            <Field label="Notes">
              {(p) => <Textarea {...p} rows={3} value={f.notes} onChange={set('notes')} maxLength={1000} />}
            </Field>
            <FormError message={missing} />
            {refused ? (
              <p
                role="alert"
                className="rounded-md border border-warning/40 bg-warning-bg px-3 py-2 text-sm text-text"
              >
                {errorMessage(err)}
              </p>
            ) : (
              <FormError message={register.isError ? errorMessage(err) : null} />
            )}
            <Button type="submit" loading={register.isPending} className="w-full sm:w-auto">
              Register lead
            </Button>
          </form>
        </Card>
      )}
    </>
  );
}

const PAGE = 25;

export function LeadsPage() {
  const [status, setStatus] = useState('');
  const [text, setText] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const q = useQuery({
    queryKey: ['leads', status, text, limit],
    queryFn: () =>
      get<{ items: LeadRow[]; total: number }>(
        `/leads?limit=${limit}${status ? `&status=${status}` : ''}${text.trim() ? `&q=${encodeURIComponent(text.trim())}` : ''}`,
      ),
    placeholderData: (prev) => prev,
  });
  const { approved } = useSession();
  return (
    <>
      <PageHeader
        title="My leads"
        actions={
          approved && (
            <Link to="/register">
              <Button>Register a lead</Button>
            </Link>
          )
        }
      />
      <div className="mb-4 grid gap-3 sm:grid-cols-[1fr_14rem]">
        <Input
          aria-label="Search by name or phone"
          placeholder="Search by name or phone"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setLimit(PAGE);
          }}
        />
        <Select
          aria-label="Show"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setLimit(PAGE);
          }}
        >
          <option value="">All leads</option>
          <option value="EXCLUSIVE">Protected for me</option>
          <option value="LAPSED">Protection ended</option>
          <option value="NOT_ACCEPTED">Not accepted</option>
        </Select>
      </div>
      {q.isLoading ? (
        <Loading />
      ) : q.isError || !q.data ? (
        <ErrorState onRetry={() => void q.refetch()} />
      ) : q.data.items.length === 0 ? (
        <EmptyState
          title="No leads here yet"
          description="Leads you register will appear here with their progress."
        />
      ) : (
        <>
          <ul className="space-y-2">
            {q.data.items.map((l) => (
              <li key={l.id}>
                <Link
                  to="/leads/$id"
                  params={{ id: l.id }}
                  className="block rounded-xl border border-border bg-surface p-3 hover:bg-surface-hover"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="min-w-0 truncate font-medium text-text">{l.name}</span>
                    <Badge tone={LEAD_STATUS[l.status]?.tone}>
                      {LEAD_STATUS[l.status]?.label ?? l.status}
                    </Badge>
                  </div>
                  <p className="mt-1 text-sm text-text-muted">
                    {l.project.name}
                    {l.stage ? ` · ${l.stage}` : ''}
                  </p>
                  <p className="mt-0.5 text-xs text-text-faint">
                    Registered {dateOnly(l.registeredAt)} by {l.registeredBy}
                    {l.status === 'EXCLUSIVE' && ` · protected until ${dateOnly(l.exclusiveUntil)}`}
                    {l.visits > 0 && ` · ${l.visits} visit${l.visits > 1 ? 's' : ''}`}
                    {l.bookings > 0 && ` · booked`}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
          {q.data.total > q.data.items.length && (
            <div className="mt-4 text-center">
              <Button variant="secondary" onClick={() => setLimit(limit + PAGE)}>
                Show more ({q.data.total - q.data.items.length} left)
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
}

export function LeadDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const money = useSession().config?.money !== false;
  const q = useQuery({ queryKey: ['lead', id], queryFn: () => get<LeadDetail>(`/leads/${id}`) });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data)
    return (
      <ErrorState
        title={
          q.error instanceof ApiError && q.error.status === 404 ? 'We could not find that lead' : undefined
        }
        onRetry={() => void q.refetch()}
      />
    );
  const l = q.data;
  const st = LEAD_STATUS[l.status];
  return (
    <>
      <p className="mb-2 text-sm">
        <Link to="/leads" className="text-primary underline">
          All leads
        </Link>
      </p>
      <PageHeader
        title={l.name}
        description={`${l.project.name} · registered ${dateOnly(l.registeredAt)} by ${l.registeredBy}`}
        actions={<Badge tone={st?.tone}>{st?.label ?? l.status}</Badge>}
      />
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="space-y-2 p-4">
          <h2 className="font-medium text-text">Where it stands</h2>
          {l.status === 'NOT_ACCEPTED' ? (
            <p className="text-sm text-text-muted">
              We could not accept this registration because this customer is already in touch with us. Your
              relationship manager can tell you more.
            </p>
          ) : (
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-text-muted">Stage</dt>
              <dd className="text-text">{l.stage ?? 'Registered'}</dd>
              <dt className="text-text-muted">Outcome</dt>
              <dd className="text-text">{l.outcome ? (OUTCOME[l.outcome] ?? l.outcome) : 'In progress'}</dd>
              <dt className="text-text-muted">Protected until</dt>
              <dd className="text-text">
                {dateOnly(l.exclusiveUntil)}
                {l.status === 'EXCLUSIVE' && (
                  <span className="text-text-faint"> ({daysLeft(l.exclusiveUntil)} days left)</span>
                )}
              </dd>
              <dt className="text-text-muted">Last visit</dt>
              <dd className="text-text">{dateTime(l.lastVisitAt)}</dd>
            </dl>
          )}
        </Card>
        <Card className="space-y-2 p-4">
          <h2 className="font-medium text-text">Customer</h2>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-text-muted">Phone</dt>
            <dd className="break-all text-text">{l.phone ?? '–'}</dd>
            <dt className="text-text-muted">Email</dt>
            <dd className="break-all text-text">{l.email ?? '–'}</dd>
            {l.notes && (
              <>
                <dt className="text-text-muted">Notes</dt>
                <dd className="whitespace-pre-line text-text">{l.notes}</dd>
              </>
            )}
          </dl>
        </Card>
        <Card className="space-y-2 p-4">
          <h2 className="font-medium text-text">Site visits</h2>
          {l.visits.length === 0 ? (
            <p className="text-sm text-text-muted">No visits yet.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {l.visits.map((v) => (
                <li key={v.id} className="flex flex-wrap justify-between gap-2">
                  <span className="text-text">
                    {dateTime(v.scheduledAt)} · {v.project}
                  </span>
                  <Badge tone={v.status === 'COMPLETED' || v.status === 'CHECKED_IN' ? 'success' : 'neutral'}>
                    {v.status.replace('_', ' ').toLowerCase()}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card className="space-y-2 p-4">
          <h2 className="font-medium text-text">{money ? 'Booking and commission' : 'Booking'}</h2>
          {l.bookings.length === 0 ? (
            <p className="text-sm text-text-muted">
              No booking yet. You are credited when this customer books.
            </p>
          ) : (
            <ul className="space-y-1 text-sm">
              {l.bookings.map((b, i) => (
                <li key={i} className="flex flex-wrap justify-between gap-2">
                  <span className="text-text">
                    Booked {dateOnly(b.bookedOn)} · {b.project}
                    {b.cancelled && ' (cancelled)'}
                  </span>
                  {money && <span className="font-medium text-text">{rupees(b.commission)}</span>}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}
